import type { AgentRuntimeSnapshot, AgentRuntimeTimelineItem } from "@cord/shared"

type ClaudeAssistantLikeTimelineItem = Extract<
  AgentRuntimeTimelineItem,
  { kind: "assistant_text" } | { kind: "thinking" }
>
type ClaudeUserTimelineItem = Extract<AgentRuntimeTimelineItem, { kind: "user_message" }>
type ClaudeToolCallTimelineItem = Extract<AgentRuntimeTimelineItem, { kind: "tool_call" }>
type ClaudeMarkerTimelineItem = Extract<AgentRuntimeTimelineItem, { kind: "marker" }>
type ClaudeErrorTimelineItem = Extract<AgentRuntimeTimelineItem, { kind: "error" }>

function measureRenderableValueLength(value: unknown): number {
  if (typeof value === "string") {
    return value.length
  }

  if (value === null || value === undefined) {
    return 0
  }

  try {
    return JSON.stringify(value).length
  } catch {
    return 0
  }
}

function shouldPreferNextClaudeTimelineItem(args: {
  current: AgentRuntimeTimelineItem
  next: AgentRuntimeTimelineItem
}): boolean {
  if (args.current.kind !== args.next.kind) {
    return false
  }

  switch (args.next.kind) {
    case "assistant_text":
    case "thinking": {
      const current = args.current as ClaudeAssistantLikeTimelineItem
      const next = args.next as ClaudeAssistantLikeTimelineItem
      if (current.streaming && !next.streaming) {
        return true
      }
      return next.text.length > current.text.length
    }
    case "user_message": {
      const current = args.current as ClaudeUserTimelineItem
      const next = args.next as ClaudeUserTimelineItem
      if (next.text.length !== current.text.length) {
        return next.text.length > current.text.length
      }

      const currentAttachmentCount = current.attachments?.length ?? current.attachmentCount ?? 0
      const nextAttachmentCount = next.attachments?.length ?? next.attachmentCount ?? 0
      if (nextAttachmentCount !== currentAttachmentCount) {
        return nextAttachmentCount > currentAttachmentCount
      }

      const currentContextRefCount = current.contextRefs?.length ?? 0
      const nextContextRefCount = next.contextRefs?.length ?? 0
      if (nextContextRefCount !== currentContextRefCount) {
        return nextContextRefCount > currentContextRefCount
      }

      return Boolean(next.actor) && !current.actor
    }
    case "tool_call": {
      const current = args.current as ClaudeToolCallTimelineItem
      const next = args.next as ClaudeToolCallTimelineItem
      if (current.status === "running" && next.status !== "running") {
        return true
      }

      const currentOutputLength = measureRenderableValueLength(current.output) + measureRenderableValueLength(current.error)
      const nextOutputLength = measureRenderableValueLength(next.output) + measureRenderableValueLength(next.error)
      if (nextOutputLength !== currentOutputLength) {
        return nextOutputLength > currentOutputLength
      }

      const currentInputSize = measureRenderableValueLength(current.input)
      const nextInputSize = measureRenderableValueLength(next.input)
      return nextInputSize > currentInputSize
    }
    case "marker": {
      const current = args.current as ClaudeMarkerTimelineItem
      const next = args.next as ClaudeMarkerTimelineItem
      return next.label.length > current.label.length
    }
    case "error": {
      const current = args.current as ClaudeErrorTimelineItem
      const next = args.next as ClaudeErrorTimelineItem
      const currentMessageLength = current.message.length + (current.code?.length ?? 0)
      const nextMessageLength = next.message.length + (next.code?.length ?? 0)
      return nextMessageLength > currentMessageLength
    }
  }
}

export function sanitizeClaudeTimelineItems(args: {
  timeline: AgentRuntimeTimelineItem[]
}): AgentRuntimeTimelineItem[] {
  if (args.timeline.length < 2) {
    return args.timeline
  }

  const deduped: AgentRuntimeTimelineItem[] = []
  const indexById = new Map<string, number>()
  let changed = false

  for (const item of args.timeline) {
    const existingIndex = indexById.get(item.id)
    if (existingIndex === undefined) {
      indexById.set(item.id, deduped.length)
      deduped.push(item)
      continue
    }

    changed = true
    const current = deduped[existingIndex]
    if (shouldPreferNextClaudeTimelineItem({ current, next: item })) {
      deduped[existingIndex] = item
    }
  }

  return changed ? deduped : args.timeline
}

export function sanitizeClaudeRuntimeSnapshot(args: {
  snapshot: AgentRuntimeSnapshot | null
}): AgentRuntimeSnapshot | null {
  if (!args.snapshot) {
    return args.snapshot
  }

  const timeline = sanitizeClaudeTimelineItems({
    timeline: args.snapshot.timeline,
  })
  if (timeline === args.snapshot.timeline) {
    return args.snapshot
  }

  return {
    ...args.snapshot,
    timeline,
  }
}
